import throttle from "lodash/throttle";
import React from "react";
import {
  Group,
  Image as KonvaImage,
  Label,
  Layer,
  Line,
  Rect,
  RegularPolygon,
  Stage,
  Tag,
  Text,
} from "react-konva";
import { useDispatch, useSelector } from "react-redux";
import { withSize } from "react-sizeme";
import uuidv4 from "uuid/v4";
import { usePrevious } from "../../../../custom-hooks/usePrevious";
import useWindowDimensions from "../../../../custom-hooks/useWindowDimensions";
import {
  CanvasMode,
  Document,
  EdgeType,
  FixedLocation,
  KonvaLine,
  KonvaRectangle,
  Percentage,
  TemplateFieldV2,
  Words,
} from "../../../../docuclipper/DocuclipperTypes";
import { ReduxState } from "../../../../redux";
import {
  addTemplateField,
  copyRectangle,
  expandRectangle,
  fetchExtractedDataFromLine,
  fetchExtractedDataFromRectangle,
  fetchWords,
  fixNegativeRectangles,
  moveRectangle,
  moveRectangleWithKeys,
  pasteRectangle,
  removeTemplateFieldFromRectangle,
  resizeRectangles,
  selectTemplateFieldOnRectangleClick,
  setCanvasMode,
  setSelectedEdge,
  setStageSize,
  transformRectangle,
  updateColumnWidthFromRectangle,
  updateEdgePercentageFromLine,
  updateTemplateField,
} from "../../../../redux/reducers/TemplateField";
import { enableTourIfNotCompleted } from "../../../../redux/reducers/Tours";
import KonvaTransformer from "../KonvaTransformer";
import { isFieldInPage } from "./RegionsUtils";

const THROTTLE_MS = 100;

function getScaledPointerPosition(stage) {
  const pointerPosition = stage.getPointerPosition();
  const x = (pointerPosition.x - stage.x()) / stage.scaleX();
  const y = (pointerPosition.y - stage.y()) / stage.scaleY();
  return { x, y };
}

type Pos = {
  x: number;
  y: number;
};

export type Box = {
  x: number;
  width: number;
  y: number;
  height: number;
  id: string;
};

export type ResizeArg = {
  previousHeight: number;
  previousWidth: number;
  height: number;
  width: number;
};

type Props = {
  selectedFieldId: string;
  scaleBy: number;
  canvasMode: CanvasMode;
  imageSrc: string;
  page: {
    width: number;
    height: number;
  };
  onRectangleDrawn: (box: Box) => any;
  onResize: (arg: ResizeArg) => any;
  rectangles: KonvaRectangle[];
  lines: KonvaLine[];

  onDragEnd: ({ x, y, id }: { x: number; y: number; id: string }) => any;
  onTransform: ({
    id,
    x,
    y,
    scaleX,
    scaleY,
    width,
    height,
  }: {
    id: string;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    width: number;
    height: number;
  }) => any;
  onTransformEnd: ({ id }: { id: string }) => any;
  onClick: (id: string) => any;
  size: {
    height: number;
    width: number;
  };
  onMouseDown: ({ x, y, id }: { x: number; y: number; id: string }) => any;
  onMouseUp: ({ x, y, id }: { x: number; y: number; id: string }) => any;
  onMouseMove: ({ x, y, id }: { x: number; y: number; id: string }) => any;
  deleteRectangle: ({ id }: { id: string }) => any;
  onLineMove: ({
    id,
    x,
    lineX,
    y,
    lineY,
  }: {
    id: string;
    x: number;
    lineX: number;
    y: number;
    lineY: number;
  }) => any;
  dragModeEnabled: boolean;
  onKeyPressed: (
    key:
      | "ctrl-c"
      | "ctrl-v"
      | "left"
      | "up"
      | "right"
      | "down"
      | "backspace"
      | "delete"
      | "selectTopEdge"
      | "selectBottomEdge"
      | "selectLeftEdge"
      | "selectRightEdge"
      | "fixed"
      | "variable"
      | "words"
  ) => any;
};

const DrawRectangle_: React.SFC<Props> = ({
  imageSrc,
  page,
  onRectangleDrawn,
  rectangles,
  lines,
  onResize,

  onDragEnd,
  onTransform,
  onTransformEnd,
  onClick,
  size,

  onMouseDown,
  onMouseMove,
  deleteRectangle,
  onLineMove,
  canvasMode,

  scaleBy,
  dragModeEnabled,

  selectedFieldId,
  onKeyPressed,
}) => {
  const [image, setImage] = React.useState<HTMLImageElement>(new Image());
  const [imageHeight, setImageHeight] = React.useState(0);
  const [imageWidth, setImageWidth] = React.useState(0);
  // const { height, width } = useWindowDimensions();

  const previousHeight = usePrevious(imageHeight) as any;
  const previousWidth = usePrevious(imageWidth) as any;

  const { width: stageWidth, height: stageHeight } = size;
  // const [selectedRectangleId, setSelectedRectangleId] = React.useState("");

  const { height: screenHeight } = useWindowDimensions();

  const cursor = React.useRef<"crosshair" | "move" | "pointer" | "">(
    canvasMode === "rectangles" ? "crosshair" : ""
  );

  React.useEffect(() => {
    cursor.current = dragModeEnabled ? "move" : "crosshair";
    if (stageRef.current) {
      if (dragModeEnabled) {
        (stageRef.current as any).container().style.cursor = "move";
      } else if (canvasMode === "viewExtractedData") {
        (stageRef.current as any).container().style.cursor = "";
      } else {
        (stageRef.current as any).container().style.cursor = "crosshair";
      }
    }
  }, [dragModeEnabled, canvasMode]);

  React.useEffect(() => {
    if (previousHeight !== imageHeight || previousWidth !== imageWidth) {
      onResize({
        previousHeight,
        previousWidth,
        height: imageHeight,
        width: imageWidth,
      });
    } else {
      // console.log("same size", {
      //   previousWidth,
      //   previousHeight,
      //   imageHeight,
      //   imageWidth,
      // });
    }
  }, [imageHeight, imageWidth, onResize, previousHeight, previousWidth]);

  React.useEffect(() => {
    const maxWidth = stageWidth; // * 0.75;

    const ratio = page.width / page.height;

    // console.log("limited by width", { stageHeight, stageWidth });

    let newWidth = Math.min(maxWidth, page.width);
    let newHeight = newWidth / ratio;
    let cappedHeight = Math.min(newHeight, screenHeight * 0.9);
    newWidth = Math.min(newWidth, cappedHeight * ratio);
    // console.log("newWidth", { width: newWidth, height: newWidth / ratio });
    setImageWidth(newWidth);
    setImageHeight(newWidth / ratio);
  }, [stageWidth, stageHeight, page.height, page.width, screenHeight]);

  const isRectangleEvent = (e) => {
    if (
      e &&
      e.target &&
      e.target.attrs &&
      e.target.attrs.fill &&
      e.target.attrs.name
    ) {
      return true;
    }
    return false;
  };

  const isLineEvent = (e) => {
    if (e && e.target && e.target.attrs && e.target.attrs.points) {
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => setImage(image);
  }, [imageSrc]);

  const startPos = React.useRef<Pos | null>(null);
  const rectangleId = React.useRef<null | string>("");
  const onMouseDownFn = (e) => {
    // console.log(e);
    if (
      e &&
      e.target &&
      e.target.parent &&
      e.target.parent.attrs &&
      e.target.parent.attrs.rotateEnabled !== undefined
    ) {
      // we're transforming, exit
      return;
    }

    if (isRectangleEvent(e)) {
      // we're moving a rectangle,  exit
      return;
    }

    if (isLineEvent(e)) {
      // we're moving a line, exit
      return;
    }

    if (!["rectangles", "selectWords"].includes(canvasMode)) {
      return;
    }

    // if (["rectangles"].includes(canvasMode)) {
    //   onClick("");
    // }

    if (!e || !e.currentTarget) {
      return;
    }

    const { x, y } = getScaledPointerPosition(e.currentTarget.getStage());
    // console.log(`down x: ${x} y: ${y}`);
    startPos.current = { x, y };
    rectangleId.current = uuidv4();
    if (rectangleId.current) {
      onMouseDown({ x, y, id: rectangleId.current });
    }
  };
  const onMouseUpFn = (e) => {
    if (!rectangleId.current) {
      return;
    }
    if (!e || !e.currentTarget) {
      return;
    }
    const { x, y } = getScaledPointerPosition(e.currentTarget.getStage());
    // console.log(`up x: ${x} y: ${y}`);

    if (
      startPos.current &&
      rectangleId.current &&
      x !== startPos.current.x &&
      y !== startPos.current.y
    ) {
      onMouseMove({ id: rectangleId.current, x, y });
      onRectangleDrawn({
        id: rectangleId.current,
        x: startPos.current.x,
        width: x - startPos.current.x,
        y: startPos.current.y,
        height: y - startPos.current.y,
      });
      if (canvasMode === "selectWords") {
        deleteRectangle({ id: rectangleId.current });
      }
    } else {
      if (rectangleId.current) {
        deleteRectangle({ id: rectangleId.current });
      }
    }

    rectangleId.current = null;
    startPos.current = null;
  };
  const onMouseMoveFn = (e) => {
    if (!rectangleId.current) {
      return;
    }
    if (!e || !e.currentTarget) {
      return;
    }
    const { x, y } = getScaledPointerPosition(e.currentTarget.getStage());
    // console.log(`move x: ${x} y: ${y}`);
    onMouseMove({ id: rectangleId.current, x, y });
  };

  const onDragEndFn = (id: string) => (e) => {
    onDragEnd({
      id,
      x: e.target.x(),
      y: e.target.y(),
    });
  };
  const onTransformFn = (id: string) => (e) => {
    if (!e || !e.currentTarget) {
      return;
    }
    onTransform({
      id,
      x: e.currentTarget.x(),
      y: e.currentTarget.y(),
      height: e.currentTarget.height(),
      width: e.currentTarget.width(),
      scaleX: e.currentTarget.scaleX(),
      scaleY: e.currentTarget.scaleY(),
    });
  };

  const onTransformEndFn = (id: string) => (e) => onTransformEnd({ id });
  const onClickFn = (id: string) => (e) => {
    // setSelectedRectangleId(id);
    onClick(id);
  };
  const onMouseMoveThrottled = React.useRef(
    throttle<any>((e) => onMouseMoveFn(e), THROTTLE_MS)
  );

  const dragBoundXFunc = ({
    originalX,
    minX,
    maxX,
  }: {
    originalX: number;
    minX: number;
    maxX: number;
  }) => {
    return (pos: { x: number; y: number }) => {
      // x
      let x = pos.x;

      if (pos.x < -(originalX - minX)) {
        x = -(originalX - minX);
      }

      if (pos.x > maxX - originalX) {
        x = maxX - originalX;
      }

      return {
        x,
        y: 0,
      };
    };
  };

  const dragBoundYFunc = ({
    originalY,
    minY,
    maxY,
  }: {
    originalX: number;
    originalY: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  }) => {
    return (pos: { x: number; y: number }) => {
      // y
      let y = pos.y;

      if (pos.y < -(originalY - minY)) {
        y = -(originalY - minY);
      }

      if (pos.y > maxY - originalY) {
        y = maxY - originalY;
      }

      return {
        x: 0,
        y,
      };
    };
  };

  const onLineDragEnd = (id: string) => (e) => {
    if (!e || !e.currentTarget) {
      return;
    }
    const { x, y } = getScaledPointerPosition(e.currentTarget.getStage());
    // console.log("attrsx", e.currentTarget.attrs.x);
    onLineMove({
      id,
      x,
      lineX: x - e.currentTarget.attrs.x,
      y,
      lineY: y - e.currentTarget.attrs.y,
    });
  };

  const onMouseEnter = (e) => {
    if (!e || !e.currentTarget) {
      return;
    }
    if (canvasMode === "rectangles") {
      e.currentTarget.getStage().container().style.cursor = "move";
    } else if (canvasMode === "viewExtractedData") {
      e.currentTarget.getStage().container().style.cursor = "pointer";
    }
  };

  const onMouseEnterLine = (lineType: KonvaLine["type"]) => (e) => {
    if (!e || !e.currentTarget) {
      return;
    }
    let cursor = "";
    if (["horizontal", "horizontalFullWidth"].includes(lineType)) {
      cursor = "ns-resize";
    } else if (["vertical", "verticalFullHeight"].includes(lineType)) {
      cursor = "ew-resize";
    }

    e.currentTarget.getStage().container().style.cursor = cursor;
  };

  const onMouseLeave = (e) => {
    if (!e || !e.currentTarget) {
      return;
    }
    // let cursor = "crosshair";
    // if (true) {
    //   cursor = "pointer";
    // }
    e.currentTarget.getStage().container().style.cursor = cursor.current;
  };

  const stageRef = React.useRef<any>(null);
  const onKeyDown = (e) => {
    e.preventDefault();
    const key = e.which || e.keyCode; // keyCode detection
    const ctrl = e.ctrlKey ? e.ctrlKey : key === 17 ? true : false; // ctrl detection

    if (key === 86 && ctrl) {
      onKeyPressed("ctrl-v");
    } else if (key === 67 && ctrl) {
      onKeyPressed("ctrl-c");
    } else if (key === 37) {
      onKeyPressed("left");
    } else if (key === 38) {
      onKeyPressed("up");
    } else if (key === 39) {
      onKeyPressed("right");
    } else if (key === 40) {
      onKeyPressed("down");
    } else if (key === 8) {
      onKeyPressed("backspace");
    } else if (key === 46) {
      onKeyPressed("delete");
    } else if (key === 84) {
      onKeyPressed("selectTopEdge");
    } else if (key === 66) {
      onKeyPressed("selectBottomEdge");
    } else if (key === 76) {
      onKeyPressed("selectLeftEdge");
    } else if (key === 82) {
      onKeyPressed("selectRightEdge");
    } else if (key === 70) {
      onKeyPressed("fixed");
    } else if (key === 86) {
      onKeyPressed("variable");
    } else if (key === 87) {
      onKeyPressed("words");
    }
  };
  return (
    <div tabIndex={1} onKeyDown={onKeyDown}>
      <Stage
        ref={(ref) => {
          stageRef.current = ref;
        }}
        onMouseDown={dragModeEnabled ? () => null : onMouseDownFn}
        onMouseUp={onMouseUpFn}
        onMouseMove={onMouseMoveThrottled.current}
        width={imageWidth}
        height={imageHeight}
        scaleX={scaleBy}
        scaleY={scaleBy}
        draggable={dragModeEnabled}
      >
        <Layer>
          <KonvaImage image={image} width={imageWidth} height={imageHeight} />
          {(rectangles || []).map(
            (
              { id, x, y, width, height, fill, draggable, scaleX, scaleY },
              i
            ) => (
              <Rect
                // style={{ cursor: "move" }}
                key={uuidv4()}
                x={x}
                y={y}
                scaleX={scaleX}
                scaleY={scaleY}
                width={width}
                height={height}
                fill={fill}
                opacity={0.5}
                draggable={
                  draggable && canvasMode === "rectangles" && !dragModeEnabled
                }
                name={id}
                onDragEnd={onDragEndFn(id)}
                onTransform={onTransformFn(id)}
                onTransformEnd={onTransformEndFn(id)}
                onMouseEnter={
                  ["rectangles", "viewExtractedData"].includes(canvasMode)
                    ? onMouseEnter
                    : () => null
                }
                onMouseLeave={
                  ["rectangles", "viewExtractedData"].includes(canvasMode)
                    ? onMouseLeave
                    : () => null
                }
                onClick={onClickFn(id)}
              />
            )
          )}
          {lines.map((l, i) => {
            let x0: number;
            let x1: number;
            let y0: number;
            let y1: number;
            let topRotation;
            let bottomRotation;
            let minX: number = 0;
            let maxX: number = imageWidth;
            let minY: number = 0;
            let maxY: number = imageHeight;
            let scaleX: number = 1.0;
            let scaleY: number = 1.0;
            switch (l.type) {
              case "horizontal":
                x0 = l.horizontalLine?.x0 || 0;
                x1 = l.horizontalLine?.x1 || 0;
                y0 = l.horizontalLine?.y || 0;
                y1 = l.horizontalLine?.y || 0;
                topRotation = 90;
                bottomRotation = 270;
                minY = l.horizontalLine?.minY || 0;
                maxY = l.horizontalLine?.maxY || imageHeight;
                scaleX = l.horizontalLine?.scaleX || 1.0;
                scaleY = l.horizontalLine?.scaleY || 1.0;
                break;
              case "horizontalFullWidth":
                x0 = 0;
                x1 = imageWidth;
                y0 = l.horizontalLine?.y || 0;
                y1 = l.horizontalLine?.y || 0;
                topRotation = 90;
                bottomRotation = 270;
                scaleX = l.horizontalLine?.scaleX || 1.0;
                scaleY = l.horizontalLine?.scaleY || 1.0;
                break;
              case "vertical":
                x0 = l.verticalLine?.x || 0;
                x1 = l.verticalLine?.x || 0;
                y0 = l.verticalLine?.y0 || 0;
                y1 = l.verticalLine?.y1 || 0;
                topRotation = 180;
                bottomRotation = 0;
                minY = l.horizontalLine?.minY || 0;
                maxY = l.horizontalLine?.maxY || imageHeight;
                minX = l.verticalLine?.minX || 0;
                maxX = l.verticalLine?.maxX || imageWidth;
                scaleX = l.verticalLine?.scaleX || 1.0;
                scaleY = l.verticalLine?.scaleY || 1.0;
                break;
              case "verticalFullHeight":
                x0 = l.verticalLine?.x || 0;
                x1 = l.verticalLine?.x || 0;
                y0 = 0;
                y1 = imageHeight;
                topRotation = 180;
                bottomRotation = 0;
                minX = l.verticalLine?.minX || 0;
                maxX = l.verticalLine?.maxX || imageWidth;
                scaleX = l.verticalLine?.scaleX || 1.0;
                scaleY = l.verticalLine?.scaleY || 1.0;
                break;
            }
            // console.log("line, ", { x0, x1, y0, y1 });
            let dragBoundFunc = (x) => x;
            if (["vertical", "verticalFullHeight"].includes(l.type)) {
              dragBoundFunc = dragBoundXFunc;
            } else if (["horizontal", "horizontalFullWidth"].includes(l.type)) {
              dragBoundFunc = dragBoundYFunc;
            }

            return (
              <Group
                key={uuidv4()}
                dragBoundFunc={dragBoundFunc({
                  originalX: l.verticalLine?.x || 0,
                  originalY: l.horizontalLine?.y || 0,
                  minX,
                  maxX,
                  minY,
                  maxY,
                })}
                draggable={l.draggable}
                onDragEnd={onLineDragEnd(l.id)}
                onMouseEnter={
                  l.draggable ? onMouseEnterLine(l.type) : () => null
                }
                onMouseLeave={l.draggable ? onMouseLeave : () => null}
              >
                <RegularPolygon
                  x={x0}
                  y={y0}
                  sides={3}
                  radius={l.strokeWidth}
                  fill={l.stroke}
                  stroke={l.stroke}
                  strokeWidth={l.strokeWidth}
                  scaleX={scaleX}
                  scaleY={scaleY}
                  rotation={topRotation}
                />
                {/* {l.text && ( */}
                <Label x={x0} y={y0 - 16} opacity={1}>
                  <Tag pointerDirection={"down"} fill={"white"}></Tag>
                  <Text
                    text={l.text}
                    fontSize={16}
                    fontFamily={"Calibri"}
                    fill={"blue"}
                  />
                </Label>
                {/* )} */}
                <Line
                  // key={uuidv4()}
                  lineCap={"round"}
                  name={l.id}
                  // x={x0}
                  // y={y0}
                  points={[x0, y0, x1, y1]}
                  stroke={l.stroke}
                  strokeWidth={l.strokeWidth}
                  dash={l.dash || undefined}

                  // scaleX={scaleX}
                  // scaleY={scaleY}
                  // width={rulerWidth}
                ></Line>
                <RegularPolygon
                  x={x1}
                  y={y1}
                  sides={3}
                  radius={l.strokeWidth}
                  fill={l.stroke}
                  stroke={l.stroke}
                  strokeWidth={l.strokeWidth}
                  scaleX={scaleX}
                  scaleY={scaleY}
                  rotation={bottomRotation}
                />
              </Group>
            );
          })}
          <KonvaTransformer
            selectedShapeName={
              ["rectangles", "viewExtractedData"].includes(canvasMode) &&
              !dragModeEnabled
                ? selectedFieldId
                : ""
            }
          />
        </Layer>
      </Stage>
    </div>
  );
};

export const DrawRectangle = withSize({ monitorHeight: true })(DrawRectangle_);

export const DrawRectangleConnected = () => {
  const dispatch = useDispatch();

  const throttledDispatch = React.useRef(throttle<any>(dispatch, THROTTLE_MS));
  const {
    templateFields,
    page,
    selectedFieldId,
    canvasMode,
    template: templateObj,
    stage,
  } = useSelector((state: ReduxState) => state.TemplateField);

  const template = templateObj.template;

  const { files } = useSelector((state: ReduxState) => state.Files);
  let document: Document | undefined;
  if (files.length > 0) {
    document = files[0].document;
  }
  const selectWordsRectangleId = React.useRef<string | null>(null);

  if (!page || !page.page) {
    return null;
  }

  const maybeField = templateFields.filter((tf) => tf.id === selectedFieldId);
  let field: TemplateFieldV2 | null = null;
  if (maybeField.length > 0) {
    field = maybeField[0];
  }
  let filteredRectangles: KonvaRectangle[] = [];
  let filteredLines: KonvaLine[] = [];

  switch (canvasMode) {
    case "rectangles":
    case "tableColumns":
      filteredRectangles = templateFields
        // .filter((tf) => tf.locationType === "fixed")
        .filter((tf) => {
          if (!page.page || !document) {
            return false;
          }
          return isFieldInPage({
            field: tf,
            currentPage: page.page.pageNumber,
            document,
          });
        })
        .map((tf) => {
          if (tf.locationType === "fixed") {
            return [(tf.fixedLocation as FixedLocation).rectangle];
          } else if (tf.locationType === "variable") {
            return tf.fragments.fragments.map((x) => x.rectangle);
          } else {
            return [];
          }
        })
        .flat();
      if (
        field &&
        document &&
        page.page &&
        isFieldInPage({
          field,
          currentPage: page.page.pageNumber,
          document,
        }) &&
        ["ocrtables", "tables"].includes(field.toolType)
      ) {
        if (
          field.locationType === "fixed" ||
          (field.locationType === "variable" &&
            field.fragments.fragments.length > 0)
        ) {
          filteredLines = field.tableConfig.columns.map((c) => c.line);
        }
      }

      if (
        field &&
        field.locationType === "variable" &&
        field.variableLocation
      ) {
        const vid = field.variableLocation.selectedId;
        const vls = field.variableLocation.variableLocations.filter(
          (x) => x.id === vid
        );
        if (vls.length > 0) {
          const vl = vls[0];
          filteredLines = filteredLines.concat(
            (["top", "bottom", "left", "right"] as EdgeType[])
              .map((edge: EdgeType) => {
                // if (vff[edge].edgeDetectionType === "percentage") {
                //   if (vff[edge].percentage) {
                //     return [(vff[edge].percentage as Percentage).line];
                //   }
                // } else if (vff[edge].edgeDetectionType === "words") {
                //   return vff[edge].words?.lines || [];
                // }
                // return [];
                let wordLines: KonvaLine[] = [];
                if (vl[edge].words) {
                  wordLines = (vl[edge].words as Words).lines || [];
                }
                let percentageLines: KonvaLine[] = [];
                if (vl[edge].percentage) {
                  percentageLines = [(vl[edge].percentage as Percentage).line];
                }
                return wordLines.concat(percentageLines);
              })
              .flat()
          );

          // filteredRectangles = field.fragments.fragments.map(
          //   (x) => x.rectangle
          // );
        }
      }

      break;
    case "selectWords":
      filteredRectangles = templateFields
        .filter(
          (tf) =>
            tf.locationType === "fixed" &&
            tf.fixedLocation &&
            tf.fixedLocation.rectangle.id === selectWordsRectangleId.current
        )
        .map((tf) => (tf.fixedLocation as FixedLocation).rectangle);
      break;
    case "edges":
      break;

    // case "tableColumns":
    //   if (
    //     field &&
    //     ["ocrtables", "tables"].includes(field.toolType) &&
    //     field.fixedLocation
    //   ) {
    //     filteredLines = field.tableConfig.columns.map((c) => c.line);
    //     filteredRectangles = [field.fixedLocation.rectangle];
    //   }
    //   break;
    case "viewExtractedData":
      filteredRectangles = templateFields
        .map((tf) => tf.fragments.fragments.map((x) => x.rectangle))
        .flat();
      break;
  }

  let selectedRectangleId = "";
  if (
    field &&
    field.locationType === "fixed" &&
    field.fixedLocation &&
    field.fixedLocation.rectangle
  ) {
    selectedRectangleId = field?.fixedLocation.rectangle.id;
  } else if (
    field &&
    field.locationType === "variable" &&
    field.fragments &&
    field.fragments.fragments &&
    field.fragments.fragments.length > 0
  ) {
    const selectedFragments = field.fragments.fragments.filter(
      (f) =>
        f.rectangle.id ===
        (field as TemplateFieldV2).fragments.selectedFragmentId
    );
    if (selectedFragments.length > 0) {
      selectedRectangleId = selectedFragments[0].rectangle.id;
    }
  }
  return (
    <DrawRectangle
      selectedFieldId={selectedRectangleId}
      scaleBy={stage ? stage.scaleBy : 1.0}
      canvasMode={canvasMode}
      imageSrc={page.page.imageSrc}
      dragModeEnabled={stage ? stage.dragModeEnabled : false}
      onResize={(x) => {
        const { height, previousHeight, width, previousWidth } = x;
        dispatch(setStageSize({ width: x.width, height: x.height }));
        dispatch(
          resizeRectangles({
            height,
            previousHeight,
            width: width,
            previousWidth,
          })
        );
      }}
      onKeyPressed={(key) => {
        const d = throttledDispatch.current;
        switch (key) {
          case "up":
            d(
              moveRectangleWithKeys({
                id: selectedRectangleId,
                deltaX: 0,
                deltaY: -4,
              })
            );
            break;
          case "down":
            d(
              moveRectangleWithKeys({
                id: selectedRectangleId,
                deltaX: 0,
                deltaY: 4,
              })
            );
            break;
          case "left":
            d(
              moveRectangleWithKeys({
                id: selectedRectangleId,
                deltaX: -4,
                deltaY: 0,
              })
            );
            break;
          case "right":
            d(
              moveRectangleWithKeys({
                id: selectedRectangleId,
                deltaX: 4,
                deltaY: 0,
              })
            );
            break;
          case "backspace":
          case "delete":
            d(removeTemplateFieldFromRectangle({ id: selectedRectangleId }));
            break;
          case "ctrl-c":
            if (selectedFieldId) {
              d(
                copyRectangle({
                  id: selectedFieldId,
                })
              );
            }

            break;
          case "ctrl-v":
            d(pasteRectangle());
            break;
          case "selectTopEdge":
            d(setSelectedEdge({ selectedEdge: "top" }));
            break;
          case "selectBottomEdge":
            d(setSelectedEdge({ selectedEdge: "bottom" }));
            break;
          case "selectLeftEdge":
            d(setSelectedEdge({ selectedEdge: "left" }));
            break;
          case "selectRightEdge":
            d(setSelectedEdge({ selectedEdge: "right" }));
            break;
          case "fixed":
            d(
              updateTemplateField({
                id: selectedFieldId || "",
                update: {
                  locationType: "fixed",
                },
              })
            );
            break;
          case "variable":
            d(
              updateTemplateField({
                id: selectedFieldId || "",
                update: {
                  locationType: "variable",
                },
              })
            );
            break;
          case "words":
            d(setCanvasMode({ canvasMode: "selectWords" }));
            break;
        }
      }}
      onClick={(id: string) => {
        dispatch(selectTemplateFieldOnRectangleClick({ id }));
        if (canvasMode === "rectangles") {
          dispatch(fetchExtractedDataFromRectangle({ id }));
        }
      }}
      onDragEnd={(x) => {
        if (canvasMode === "rectangles") {
          dispatch(moveRectangle(x));
          dispatch(fetchExtractedDataFromRectangle({ id: x.id }));
        }
      }}
      onTransform={(x) => {
        if (canvasMode === "rectangles") {
          throttledDispatch.current(transformRectangle(x));
        }
      }}
      onTransformEnd={({ id }) =>
        dispatch(fetchExtractedDataFromRectangle({ id }))
      }
      onRectangleDrawn={(x) => {
        // console.log("rectangle drawn", x);
        dispatch(fixNegativeRectangles(x));
        if (["selectWords"].includes(canvasMode)) {
          dispatch(fetchWords(x));
          dispatch(removeTemplateFieldFromRectangle(x));
          dispatch(setCanvasMode({ canvasMode: "rectangles" }));
        } else {
          dispatch(selectTemplateFieldOnRectangleClick(x));
          dispatch(fetchExtractedDataFromRectangle({ id: x.id }));
          dispatch(
            enableTourIfNotCompleted(
              template && template.type === "Invoice"
                ? "templateSecondPartInvoice"
                : template
                ? "templateSecondPart"
                : ""
            )
          );
        }
      }}
      page={page.page}
      rectangles={filteredRectangles}
      onMouseDown={(x) => {
        if (["rectangles", "selectWords"].includes(canvasMode)) {
          dispatch(addTemplateField(x));
        }
        if (["selectWords"].includes(canvasMode)) {
          selectWordsRectangleId.current = x.id;
        }
      }}
      onMouseMove={(x) => {
        // if (canvasMode === "rectangles") {
        dispatch(expandRectangle(x));
        // }
      }}
      onMouseUp={() => null}
      deleteRectangle={(x) => {
        if (canvasMode === "rectangles") {
          dispatch(removeTemplateFieldFromRectangle(x));
          selectWordsRectangleId.current = null;
        }
      }}
      lines={filteredLines}
      onLineMove={({ id: lineId, x, lineX, y, lineY }) => {
        // if (canvasMode === "tableColumns") {
        dispatch(
          updateColumnWidthFromRectangle({
            lineId,
            x,
            lineX,
            id: maybeField[0].id,
          })
        );
        // } else if (canvasMode === "edges") {
        //   if (selectedEdge) {
        dispatch(
          updateEdgePercentageFromLine({
            x,
            lineX,
            y,
            lineY,
            id: maybeField[0].id,
            lineId,
          })
        );

        dispatch(fetchExtractedDataFromLine({ id: lineId }));

        // }
        // }
      }}
    />
  );
};
